import React, { useEffect, useState } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { Header } from "@/components/store/Header";
import { Footer } from "@/components/store/Footer";
import { GlobalPromoStrip } from "@/components/store/GlobalPromoStrip";
import { ChatWidget } from "@/components/store/ChatWidget";
import { SocialLinks } from "@/components/store/SocialLinks";
import Home from "@/pages/Home";
import OpeningSoon from "@/pages/OpeningSoon";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Wishlist from "@/pages/Wishlist";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";
import Categories from "@/pages/Categories";
import Gallery from "@/pages/Gallery";
import Admin from "@/pages/Admin";
import POS from "@/pages/POS";
import ScanPhotos from "@/pages/ScanPhotos";
import PayPage from "@/pages/PayPage";
import { HowItWorks, DriveThruPickup, About, FAQ, Contact, CMSPage, NotFound } from "@/pages/StaticPages";

const Layout = () => (
  <>
    <Header />
    <GlobalPromoStrip />
    <Outlet />
    <Footer />
    <ChatWidget />
  </>
);

const AdminLayout = () => (
  <>
    <Header />
    <Outlet />
  </>
);

const OpeningSoonLayout = ({ settings }) => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="opening-soon-layout">
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
        <img src={mediaUrl(settings.logo_url || "/images/logo.png")} alt="Barrhaven Dealswagon Outlet" className="h-12 w-auto" />
      </div>
    </header>
    <div className="flex-1"><OpeningSoon settings={settings} /></div>
    <footer className="bg-brand-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-300">
        <div>© {new Date().getFullYear()} Barrhaven Dealswagon Outlet · {settings.opening_soon_location}</div>
        <SocialLinks settings={settings} iconClass="h-5 w-5" />
      </div>
    </footer>
  </div>
);

function AppRouter() {
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ opening_soon_enabled: false }));
  }, []);

  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (!settings) return <div className="min-h-screen" />;

  const gated = settings.opening_soon_enabled;

  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="/pos" element={<POS />} />
      <Route path="/scan-photos" element={<ScanPhotos />} />
      <Route path="/pay/:token" element={<PayPage />} />
      {gated ? (
        <Route path="*" element={<OpeningSoonLayout settings={settings} />} />
      ) : (
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/deals" element={<Shop presetBadge="HOT DEAL" title="Today's Deals" subtitle="Warehouse pricing across the store." />} />
          <Route path="/new-arrivals" element={<Shop presetBadge="NEW ARRIVAL" title="New Arrivals" subtitle="Fresh off the truck." />} />
          <Route path="/clearance" element={<Shop presetBadge="CLEARANCE" title="Clearance" subtitle="Final markdowns — while they last." />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/pickup" element={<DriveThruPickup />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/returns" element={<CMSPage slug="returns" fallbackTitle="Returns" />} />
          <Route path="/privacy" element={<CMSPage slug="privacy" fallbackTitle="Privacy Policy" />} />
          <Route path="/terms" element={<CMSPage slug="terms" fallbackTitle="Terms of Service" />} />
          <Route path="/store-policies" element={<CMSPage slug="store-policies" fallbackTitle="Store Policies" />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/account" element={<Account />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      )}
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Toaster position="top-right" richColors closeButton />
          <AppRouter />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
